import { useMemo } from 'react';

import RouteStatusSection from 'components/logistics/fulfillmentLifecycle/components/RouteStatusSection';
import { categorizeRoutes } from 'components/logistics/fulfillmentLifecycle/utils/routeHelpers';
import dayjs from 'utils/dayjsConfig';
/**
 * Section configuration: order, titles, defaults, and variants.
 */
const SECTIONS = [
    { key: 'active', title: 'Active', defaultOpen: true, variant: 'active' },
    { key: 'upcoming', title: 'Upcoming', defaultOpen: false, variant: 'upcoming' },
    { key: 'unrouted', title: 'Unrouted', defaultOpen: false, variant: 'unrouted' },
    { key: 'complete', title: 'Complete', defaultOpen: false, variant: 'complete' },
];

/**
 * Compute the reference time for active-vs-upcoming categorization.
 * Uses selectedDate (YYYY-MM-DD) so routes for past/future dates are
 * classified correctly instead of always using wall-clock today.
 */
function getCategorizationReferenceTime(selectedDate) {
    const selected = selectedDate ? dayjs(selectedDate) : dayjs();
    const today = dayjs();
    if (selected.isBefore(today, 'day')) {
        return selected.endOf('day').toDate();
    }
    if (selected.isAfter(today, 'day')) {
        return selected.startOf('day').toDate();
    }
    return today.toDate();
}

/**
 * RoutesGrid
 *
 * Container component that categorizes routes into status sections
 * (Active, Upcoming, Unrouted, Complete) and renders collapsible
 * RouteStatusSection components for each non-empty group.
 * Re-categorizes when routes change (e.g. from the 15s API poll).
 *
 * @param {Object} props
 * @param {Object[]} props.routes - Array of route objects (already client-side filtered)
 * @param {string} props.selectedDate - Operating date (YYYY-MM-DD) for active vs upcoming classification
 * @param {number} props.totalFromApi - Total route count from API before client-side filters
 * @param {Function} [props.onRouteClick] - Click handler for individual route cards
 */
function RoutesGrid({ onRouteClick, routes, selectedDate, totalFromApi = 0 }) {
    // Memoize referenceTime so a new Date object is only created when inputs actually
    // change. Including `routes` as a dependency ensures the "today" case picks up a
    // fresh wall-clock time whenever the 15s poll delivers new route data (keeping
    // "Pull Xm ago" fresh), while remaining stable across unrelated parent re-renders
    // so that memo(RouteCard) is not defeated by a new referenceTime prop every render.
    const referenceTime = useMemo(() => getCategorizationReferenceTime(selectedDate), [selectedDate, routes]);
    const categorized = useMemo(() => categorizeRoutes(routes, referenceTime), [routes, referenceTime]);

    const totalRoutes = routes.length;
    const hasAnyRoutes = totalRoutes > 0;
    const filtersNarrowedToZero = totalFromApi > 0 && totalRoutes === 0;

    return (
        <div className="space-y-2">
            {hasAnyRoutes ? (
                SECTIONS.map((section) => (
                    <RouteStatusSection
                        key={section.key}
                        title={section.title}
                        variant={section.variant}
                        routes={categorized[section.key]}
                        defaultOpen={section.defaultOpen}
                        onRouteClick={onRouteClick}
                        referenceTime={referenceTime}
                    />
                ))
            ) : filtersNarrowedToZero ? (
                <p className="text-sm text-muted-foreground text-center py-8">
                    No routes match the current filters. Try adjusting the status or milestone filter.
                </p>
            ) : (
                <p className="text-sm text-muted-foreground text-center py-8">
                    No routes for this date. Try selecting a different date.
                </p>
            )}
        </div>
    );
}

export default RoutesGrid;
